import { ZipCodeLocalAreaModel } from 'api/model';
import { useZipCodeLocalAreaGetZipCodeLocalAreas } from 'api/zip-code-local-area/zip-code-local-area';
import { useMemo } from 'react';
import { makeStringComparator } from 'utils/sorting';

const useZipCodeLocalAreas = () => {
  const { data: zipCodeLocalAreas } = useZipCodeLocalAreaGetZipCodeLocalAreas();

  const allZipCodeLocalAreas = useMemo(
    () =>
      zipCodeLocalAreas
        ? [...zipCodeLocalAreas].sort(makeStringComparator((x) => x.name))
        : [],
    [zipCodeLocalAreas]
  );

  const zipCodeLocalAreaByName = useMemo(() => {
    const areasByName: Record<string, ZipCodeLocalAreaModel> = {};
    allZipCodeLocalAreas.forEach((zcla) => {
      if (!(zcla.name in areasByName)) {
        areasByName[zcla.name] = zcla;
      }
    });
    return areasByName;
  }, [allZipCodeLocalAreas]);

  const selectableZipCodeLocalAreas = useMemo(
    () =>
      Object.values(zipCodeLocalAreaByName).sort(
        makeStringComparator((x) => x.name)
      ),
    [zipCodeLocalAreaByName]
  );

  const zipCodeLocalAreaFromZipCode = (zipCode: string) => {
    const zipNumber = Number(zipCode.replace(/\s/g, ''));
    if (!Number.isNaN(zipNumber)) {
      const area = allZipCodeLocalAreas.find(
        (zcla) =>
          Array.isArray(zcla.zipCodeRules) &&
          zcla.zipCodeRules.some(
            (rule) =>
              rule.minCode !== undefined &&
              rule.maxCode !== undefined &&
              rule.minCode <= zipNumber &&
              rule.maxCode >= zipNumber
          )
      );
      return area ? zipCodeLocalAreaByName[area.name] : undefined;
    }
    return undefined;
  };

  const zipCodeLocalAreaNames = zipCodeLocalAreas
    ? zipCodeLocalAreas.map((zcla) => zcla.name)
    : [];

  return {
    allZipCodeLocalAreas,
    zipCodeLocalAreaByName,
    selectableZipCodeLocalAreas,
    zipCodeLocalAreaNames,
    zipCodeLocalAreaFromZipCode,
  };
};

export default useZipCodeLocalAreas;
